import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Container, Button, Divider, Grid,
} from "@mui/material";
import { MdVerified, MdErrorOutline, MdOutlineCalendarToday, MdOutlinePerson, MdArrowBack, MdEmojiEvents} from "react-icons/md";
import { useVerifyCertificateQuery } from "../../slice/logdetailSlice";
import dayjs from "dayjs";
import "../../App.css";

const VerifyCertificate = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { data: certData, isLoading, isError, error } = useVerifyCertificateQuery(token);
    // const { data: certificates = [] } = useFetchCertificatesQuery();

    
    // Scroll to top on load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                sx={{ background: "#f0f4f8" }}
            >
                <CircularProgress sx={{ color: "#04a189", mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                    Verifying Certificate...
                </Typography>
            </Box>
        );
    }

    if (isError || !certData) {
        return (
            <Container maxWidth="sm" sx={{ minHeight: "100vh", display: "flex", alignItems: "center" }}>
                <Paper
                    elevation={6}
                    sx={{
                        p: 4,
                        textAlign: "center",
                        borderRadius: 4,
                        borderTop: "6px solid #ed2828ff",
                        width: '100%'
                    }}
                >
                    <MdErrorOutline size={80} color="#ed2828ff" />
                    <Typography variant="h4" gutterBottom sx={{ mt: 2, fontWeight: 700 }}>
                        Verification Failed
                    </Typography>
                    <Typography variant="body1" color="textSecondary" paragraph>
                        {error?.data?.message || "This certificate token is invalid or has expired. Please scan the official QR code."}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.history.back()}
                        startIcon={<MdArrowBack />}
                        sx={{
                            mt: 2,
                            backgroundColor: "#04a189",
                            textTransform: 'none',
                            px: 4,
                            "&:hover": { backgroundColor: "#038d77" }
                        }}
                    >
                        Back to Home
                    </Button>
                </Paper>
            </Container>
        );
    }

    // Safely extract data directly from certData
    // Map data from the provided API structure
    const award_name = certData?.award_name || "";
    const employee_name = certData?.recipient_name || certData?.employee_name || "";
    const award_year = certData?.award_year || "";
    const period_label = (certData?.period_label && certData.period_label !== "") 
        ? certData.period_label 
        : (certData?.period !== "" ? certData?.period : "");
    const awarded_date = certData?.generated_at || certData?.awarded_date || null;
    const certificate_url = certData?.certificate_url || "";

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 4, md: 8 },
                background: "#f0f4f8",
                display: "flex",
                alignItems: "center"
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "24px",
                        overflow: "hidden",
                        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                        bgcolor: 'white'
                    }}
                >
                    {/* Verified Header Area */}
                    <Box
                        sx={{
                            background: "linear-gradient(135deg, #04a189 0%, #038d77 100%)",
                            pt: 4,
                            pb: 3,
                            textAlign: "center",
                            color: "white",
                            position: "relative"
                        }}
                    >
                        {/* Decorative background shapes */}
                        <Box sx={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                        <Box sx={{ position: 'absolute', bottom: -20, left: -10, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                        <MdVerified size={56} style={{ marginBottom: "12px", filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.1))' }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 1, mb: 0.5 }}>
                            VERIFIED
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase' }}>
                            OFFICIAL DIGITAL RECORD
                        </Typography>
                    </Box>

                    <Box sx={{ p: { xs: 3, md: 4 } }}>
                        {/* Award Name with Trophy */}
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={3}>
                            <MdEmojiEvents size={36} color="#fbbf24" />
                            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e3a8a", textAlign: 'center' }}>
                                {award_name}
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        {/* Details Grid */}
                        <Grid container spacing={4}>
                            {/* Row 1 */}
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#7f8c8d", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                    Issued To
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <MdOutlinePerson size={22} color="#1e3a8a" />
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                                        {employee_name}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#7f8c8d", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                    Period
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <MdOutlineCalendarToday size={20} color="#1e3a8a" />
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                                        {period_label}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Row 2 */}
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#7f8c8d", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                    Awarded Year
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <MdOutlineCalendarToday size={20} color="#1e3a8a" />
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                                        {award_year}
                                    </Typography>
                                </Box>
                            </Grid>
                            
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#7f8c8d", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                    Awarded Date
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <MdOutlineCalendarToday size={20} color="#1e3a8a" />
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                                        {awarded_date ? dayjs(awarded_date).format("DD MMMM YYYY") : ""}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Certificate Preview */}
                        {certificate_url && (
                            <Box sx={{ mt: 4, mb: 2 }}>
                                <Typography variant="caption" sx={{ color: "#7f8c8d", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: 'block', mb: 1, textAlign: 'center' }}>
                                    Certificate Preview
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1.414 / 1',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0',
                                        bgcolor: '#ffffff',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        position: 'relative',
                                        pointerEvents: 'none', // Prevents focus and intercepting shortcuts
                                        userSelect: 'none'
                                    }}
                                >
                                    <iframe
                                        src={/iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                                            ? `https://docs.google.com/gview?url=${encodeURIComponent(certificate_url)}&embedded=true`
                                            : `${certificate_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                        title="Certificate Preview"
                                        width="100%"
                                        height="100%"
                                        style={{ 
                                            border: 'none', 
                                            overflow: 'hidden',
                                            backgroundColor: '#ffffff'
                                        }}
                                        scrolling="no"
                                    />
                                </Box>
                            </Box>  
                        )}

                        {/* Token Reference */}
                        <Box sx={{ textAlign: 'center', mb: 5, mt: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#04a189', letterSpacing: 0.5 }}>
                                Verification Token Reference: <span style={{ color: '#04a189' }}>{token.toUpperCase()}</span>
                            </Typography>
                        </Box>

                        {/* Back Button */}
                        <Box display="flex" justifyContent="center">
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => window.history.back()}
                                sx={{
                                    borderRadius: "8px",
                                    px: 6,
                                    py: 1.2,
                                    bgcolor: "#04a189",
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    '&:hover': {
                                        bgcolor: "#048774ff"
                                    }
                                }}
                            >
                                Back to Home
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Typography variant="caption" align="center" display="block" sx={{ mt: 4, color: "#94a3b8", fontWeight: 500 }}>
                    © {new Date().getFullYear()} Techno Tackle Software Solutions. This is an automatically generated verification page.
                </Typography>
            </Container>
        </Box>
    );
};

export default VerifyCertificate;
